import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Button,
  Form,
  Row,
  Col,
  Table,
  Alert,
  ListGroup,
  Badge,
  Toast,
  ToastContainer,
} from 'react-bootstrap';
import ElegantModal from '../../components/ElegantModal/ElegantModal';
import { urlApiProd } from '../utiles';
import { parsePackingListExcel, formatM3Display } from './parsePackingListExcel';

const fechaHoy = () => {
  const t = new Date();
  const y = t.getFullYear();
  const m = String(t.getMonth() + 1).padStart(2, '0');
  const d = String(t.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** N° de PI (texto de contrato en Excel/PDF), alineado con el backend. */
function getPiLabel(idProforma, proformas) {
  if (!idProforma) return '';
  const pi = proformas.find((p) => String(p.id_proforma) === String(idProforma));
  if (!pi) return '';
  return proformaEtiqueta(pi);
}

function proformaEtiqueta(pi) {
  if (!pi) return '';
  return (pi.id_pi && String(pi.id_pi).trim()) || `PI #${pi.id_proforma}`;
}

/** Etiqueta + id interno para buscar por correlativo o número. */
function proformaBusquedaHaystack(pi) {
  if (!pi) return '';
  const lbl = proformaEtiqueta(pi);
  const idn = pi.id_proforma != null ? String(pi.id_proforma) : '';
  return `${lbl} ${idn}`.trim();
}

/** Último id de la lista ordenada de selección (string vacío si no hay). */
function ultimaProformaId(ids) {
  if (!ids?.length) return '';
  return String(ids[ids.length - 1]);
}

/** Mueve `id` al final si ya existía; si no, lo agrega. */
function appendProformaIdOrdenado(prevIds, id) {
  const s = String(id);
  const next = (prevIds || []).filter((x) => String(x) !== s);
  next.push(s);
  return next;
}

/** Etiquetas PI únicas ordenadas (contratos preseleccionados para columna Contract). */
function contratosPreseleccionadosLabels(proformasLista) {
  if (!proformasLista?.length) return [];
  const labels = proformasLista.map((p) => proformaEtiqueta(p)).filter(Boolean);
  return [...new Set(labels)].sort((a, b) =>
    a.localeCompare(b, 'es', { sensitivity: 'base' })
  );
}

/** Categoría en inglés en mayúsculas (primer producto de la PI), columna Products. */
function getDefaultProductsCategoryEnFromPi(idProforma, proformas, productos, categorias) {
  if (!idProforma || !proformas?.length || !productos?.length || !categorias?.length) {
    return '';
  }
  const pi = proformas.find((p) => String(p.id_proforma) === String(idProforma));
  if (!pi || !pi.productos || !pi.productos.length) return '';
  const pid = pi.productos[0];
  const prod = productos.find((p) => String(p.id_producto) === String(pid));
  if (!prod) return '';
  const cat = categorias.find((c) => String(c.id_categoria) === String(prod.categoria));
  if (!cat) return '';
  const name = (cat.nombre_ingles && String(cat.nombre_ingles).trim()) || cat.nombre || '';
  return name ? name.toUpperCase() : '';
}

/** Clave contenedor para fusionar filas consecutivas (Gross kg, Net weight, Container). */
function normContRow(r) {
  if (!r) return '';
  return String(r.container_celda ?? r._container_excel ?? '').trim();
}

function groupStartContRows(rows, idx) {
  const k = normContRow(rows[idx]);
  if (!k) return idx;
  let s = idx;
  while (s > 0 && normContRow(rows[s - 1]) === k) s -= 1;
  return s;
}

function groupEndContRows(rows, idx) {
  const k = normContRow(rows[idx]);
  if (!k) return idx;
  let e = idx;
  while (e < rows.length - 1 && normContRow(rows[e + 1]) === k) e += 1;
  return e;
}

function containerMergeInfo(rows, idx) {
  const k = normContRow(rows[idx]);
  if (!k) {
    return { isFirst: true, span: 1, start: idx, end: idx };
  }
  const start = groupStartContRows(rows, idx);
  const end = groupEndContRows(rows, idx);
  return { isFirst: start === idx, span: end - start + 1, start, end };
}

function containerFieldsFromInput(v) {
  const t = v != null ? String(v).trim() : '';
  if (!t) {
    return { container_celda: '', _container_excel: '', _container: null };
  }
  const n = parseFloat(String(t).replace(',', '.'));
  return {
    container_celda: t,
    _container_excel: t,
    _container: Number.isFinite(n) ? n : null,
  };
}

function isRwNrRow(row) {
  const txt = `${String(row?.productos_texto ?? '')} ${String(row?.grado_celda ?? '')}`.toUpperCase();
  return /\b(RW\/NR|RW|WR)\b/.test(txt);
}

function cmpClienteNombre(a, b) {
  return (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' });
}

/** Texto normalizado para búsqueda: minúsculas y sin acentos (p. ej. "méxico" ≡ "mexico"). */
function foldBusquedaTexto(s) {
  return String(s ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

/** Clave para unir variantes (mayúsculas, acentos, espacios). */
function paisCatalogoDedupKey(s) {
  return (s || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/\s+/g, ' ');
}

/** Primera letra de cada palabra (y tras guión) en mayúscula; resto en minúsculas. */
function titleCasePaisNombre(s) {
  const t = (s || '').trim().toLowerCase();
  if (!t) return '';
  return t
    .split(/\s+/)
    .filter(Boolean)
    .map((w) =>
      w
        .split('-')
        .map((seg) => (seg ? seg.charAt(0).toUpperCase() + seg.slice(1) : ''))
        .join('-')
    )
    .join(' ');
}

// Parche exclusivo de Packing List: mostrar Brazil cuando el país sea Brasil.
function normalizeCountryForPackingList(s) {
  const t = (s || '').trim();
  if (!t) return '';
  return /^brasil$/i.test(t) ? 'Brazil' : t;
}

function buildPaisesCatalogoFromRaw(valores) {
  const byKey = new Map();
  for (const raw0 of valores) {
    const raw = String(raw0 ?? '').trim();
    if (raw.length < 3) continue;
    const key = paisCatalogoDedupKey(raw);
    if (!key) continue;
    const prev = byKey.get(key);
    if (!prev || raw.length > prev.length) byKey.set(key, raw);
  }
  const normalizedUnique = new Map();
  for (const r of Array.from(byKey.values())) {
    const normalized = normalizeCountryForPackingList(titleCasePaisNombre(r));
    if (!normalized) continue;
    const nKey = paisCatalogoDedupKey(normalized);
    if (!normalizedUnique.has(nKey)) normalizedUnique.set(nKey, normalized);
  }
  return Array.from(normalizedUnique.values())
    .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
}

function filtrarPaisesCatalogo(catalogo, texto) {
  const qf = foldBusquedaTexto(texto);
  if (!qf) return catalogo;
  return catalogo
    .filter((n) => foldBusquedaTexto(n).includes(qf))
    .slice(0, 80);
}

function puertoEtiqueta(p) {
  if (!p) return '';
  const n = (p.nombre || '').trim();
  const pa = normalizeCountryForPackingList((p.pais || '').trim());
  return pa ? `${n} (${pa})` : n;
}

function cmpPuerto(a, b) {
  const byNombre = (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' });
  if (byNombre !== 0) return byNombre;
  return (a.pais || '').localeCompare(b.pais || '', 'es', { sensitivity: 'base' });
}

/**
 * Mantenedor Puertos: 1 = Origen, 2 = Destino, 3 = Origen-Destino (debe listarse en ambos).
 * {@link frontend/src/vistas/puertos/AgregarPuerto.js}
 */
function puertoIndicadorNum(p) {
  const n = Number(p?.indicador);
  return Number.isFinite(n) ? n : NaN;
}

function puertoEnListaOrigenMantenedor(p) {
  const n = puertoIndicadorNum(p);
  return n === 1 || n === 3;
}

function puertoEnListaDestinoMantenedor(p) {
  const n = puertoIndicadorNum(p);
  return n === 2 || n === 3;
}

/**
 * Formulario creación packing list en diálogo ElegantModal.
 * Validaciones: N° booking obligatorio y único; BL alfanumérico, único por N° (vs lista existente).
 */
const AgregarPackingList = ({
  show,
  cerrar,
  packingListEditId = null,
  existingBookingNumbers = [],
  existingBlPorInvoice = [],
  onGuardarExito,
}) => {
  const isEditMode = packingListEditId != null && packingListEditId !== '';
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [puertos, setPuertos] = useState([]);
  const [proformas, setProformas] = useState([]);
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);

  const [numeroBooking, setNumeroBooking] = useState('');
  const [fechaEmbarque, setFechaEmbarque] = useState(fechaHoy());
  const [idCliente, setIdCliente] = useState('');
  const [idProveedor, setIdProveedor] = useState('');
  const [paisOrigen, setPaisOrigen] = useState('');
  const [paisDestino, setPaisDestino] = useState('');
  const [nombreNave, setNombreNave] = useState('');
  const [idPuertoOrigen, setIdPuertoOrigen] = useState('');
  const [idPuertoDestino, setIdPuertoDestino] = useState('');
  const [lugarEntrega, setLugarEntrega] = useState('');
  const [nombreBl, setNombreBl] = useState('');
  /** Orden de selección: la última PI es la que alimenta Contract/Products por defecto. */
  const [idsProformaSeleccion, setIdsProformaSeleccion] = useState([]);
  const [archivoPacking, setArchivoPacking] = useState(null);
  const [totalPaquetes, setTotalPaquetes] = useState('');
  const [totalContenedores, setTotalContenedores] = useState('');
  const [detalleExcel, setDetalleExcel] = useState([]);
  const [parseMeta, setParseMeta] = useState(null);
  const [archivoParseError, setArchivoParseError] = useState('');

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showGuardadoToast, setShowGuardadoToast] = useState(false);
  const [guardadoToastMensaje, setGuardadoToastMensaje] = useState('');
  const [clienteBusqueda, setClienteBusqueda] = useState('');
  const [clienteMenuOpen, setClienteMenuOpen] = useState(false);
  const [paisOrigenMenuOpen, setPaisOrigenMenuOpen] = useState(false);
  const [paisDestinoMenuOpen, setPaisDestinoMenuOpen] = useState(false);
  const [puertoOrigenBusqueda, setPuertoOrigenBusqueda] = useState('');
  const [puertoOrigenMenuOpen, setPuertoOrigenMenuOpen] = useState(false);
  const [puertoDestinoBusqueda, setPuertoDestinoBusqueda] = useState('');
  const [puertoDestinoMenuOpen, setPuertoDestinoMenuOpen] = useState(false);
  const [proformaBusqueda, setProformaBusqueda] = useState('');
  const [proformaMenuOpen, setProformaMenuOpen] = useState(false);
  const proformaInputRef = useRef(null);
  /** Tras elegir en la lista, el blur del input no debe cerrar el menú enseguida. */
  const proformaElegidaRecienteRef = useRef(false);
  const bookingOriginalRef = useRef('');
  const blOriginalRef = useRef('');
  const packingModalLoadKeyRef = useRef(null);

  const idsProformaRef = useRef(idsProformaSeleccion);
  const proformasRef = useRef(proformas);
  const idClienteRef = useRef(idCliente);
  useEffect(() => {
    idsProformaRef.current = idsProformaSeleccion;
  }, [idsProformaSeleccion]);
  useEffect(() => {
    proformasRef.current = proformas;
  }, [proformas]);
  useEffect(() => {
    idClienteRef.current = idCliente;
  }, [idCliente]);

  useEffect(() => {
    if (show) {
      setShowGuardadoToast(false);
      setGuardadoToastMensaje('');
    }
  }, [show]);

  /** Evita pisar Contract al solo cargar catálogo proformas; sí sincroniza al cambiar PIs o Excel. */
  const contractSyncRef = useRef({ idsKey: '[]', parseMeta: null, label: '' });

  const productosRef = useRef(productos);
  const categoriasRef = useRef(categorias);
  useEffect(() => {
    productosRef.current = productos;
  }, [productos]);
  useEffect(() => {
    categoriasRef.current = categorias;
  }, [categorias]);

  /** PIs cuyo campo cliente coincide con el seleccionado; sin cliente, todas. */
  const proformasFiltradas = useMemo(() => {
    if (!idCliente) return proformas;
    return proformas.filter(
      (p) => p.cliente != null && String(p.cliente) === String(idCliente)
    );
  }, [proformas, idCliente]);

  const idProformaUltima = useMemo(
    () => ultimaProformaId(idsProformaSeleccion),
    [idsProformaSeleccion]
  );

  /** Contratos (PI) disponibles para columna Contract del Excel: solo PIs seleccionadas. */
  const contratosExcelOpciones = useMemo(
    () => {
      if (!idsProformaSeleccion?.length) return [];
      const sel = new Set(idsProformaSeleccion.map((id) => String(id)));
      const proformasSel = (proformas || []).filter((p) => sel.has(String(p.id_proforma)));
      return contratosPreseleccionadosLabels(proformasSel);
    },
    [idsProformaSeleccion, proformas]
  );

  const clientesFiltrados = useMemo(() => {
    const qf = foldBusquedaTexto(clienteBusqueda);
    if (!qf) {
      return [...clientes].sort(cmpClienteNombre);
    }
    return clientes
      .filter((c) => foldBusquedaTexto(c.nombre || '').includes(qf))
      .sort(cmpClienteNombre)
      .slice(0, 80);
  }, [clientes, clienteBusqueda]);

  const paisesOrigenCatalogo = useMemo(
    () =>
      buildPaisesCatalogoFromRaw([
        ...proveedores.map((p) => p.pais),
        ...puertos.map((p) => p.pais),
      ]),
    [proveedores, puertos]
  );

  const paisesDestinoCatalogo = useMemo(
    () =>
      buildPaisesCatalogoFromRaw([
        ...clientes.map((c) => c.pais),
        ...puertos.map((p) => p.pais),
      ]),
    [clientes, puertos]
  );

  const paisesOrigenFiltrados = useMemo(
    () => filtrarPaisesCatalogo(paisesOrigenCatalogo, paisOrigen),
    [paisesOrigenCatalogo, paisOrigen]
  );

  const paisesDestinoFiltrados = useMemo(
    () => filtrarPaisesCatalogo(paisesDestinoCatalogo, paisDestino),
    [paisesDestinoCatalogo, paisDestino]
  );

  const puertosOrdenados = useMemo(
    () => [...puertos].sort(cmpPuerto),
    [puertos]
  );

  /** Según `indicador` del mantenedor de puertos (Origen, Destino u Origen-Destino en ambas listas). */
  const puertosCatalogoOrigen = useMemo(
    () => puertosOrdenados.filter((p) => puertoEnListaOrigenMantenedor(p)),
    [puertosOrdenados]
  );

  const puertosCatalogoDestino = useMemo(
    () => puertosOrdenados.filter((p) => puertoEnListaDestinoMantenedor(p)),
    [puertosOrdenados]
  );

  const puertosOrigenFiltrados = useMemo(() => {
    const qf = foldBusquedaTexto(puertoOrigenBusqueda);
    if (!qf) return puertosCatalogoOrigen;
    return puertosCatalogoOrigen
      .filter((p) => foldBusquedaTexto(puertoEtiqueta(p)).includes(qf))
      .slice(0, 80);
  }, [puertosCatalogoOrigen, puertoOrigenBusqueda]);

  const puertosDestinoFiltrados = useMemo(() => {
    const qf = foldBusquedaTexto(puertoDestinoBusqueda);
    if (!qf) return puertosCatalogoDestino;
    return puertosCatalogoDestino
      .filter((p) => foldBusquedaTexto(puertoEtiqueta(p)).includes(qf))
      .slice(0, 80);
  }, [puertosCatalogoDestino, puertoDestinoBusqueda]);

  const proformasOrdenadas = useMemo(
    () =>
      [...proformasFiltradas].sort((a, b) =>
        proformaEtiqueta(a).localeCompare(proformaEtiqueta(b), 'es', { sensitivity: 'base' })
      ),
    [proformasFiltradas]
  );

  const proformasBusquedaLista = useMemo(() => {
    const yaSeleccion = new Set((idsProformaSeleccion || []).map((id) => String(id)));
    const candidatas = proformasOrdenadas.filter(
      (pi) => !yaSeleccion.has(String(pi.id_proforma))
    );
    const qf = foldBusquedaTexto(proformaBusqueda);
    if (!qf) return candidatas.slice(0, 80);
    return candidatas
      .filter((pi) => foldBusquedaTexto(proformaBusquedaHaystack(pi)).includes(qf))
      .slice(0, 80);
  }, [proformasOrdenadas, proformaBusqueda, idsProformaSeleccion]);

  useEffect(() => {
    setIdsProformaSeleccion((prev) =>
      prev.filter((id) =>
        proformasFiltradas.some((p) => String(p.id_proforma) === String(id))
      )
    );
  }, [idCliente, proformasFiltradas]);

  const defaultProductsLabel = useMemo(
    () =>
      getDefaultProductsCategoryEnFromPi(
        idProformaUltima,
        proformas,
        productos,
        categorias
      ),
    [idProformaUltima, proformas, productos, categorias]
  );

  const resetForm = useCallback(() => {
    setNumeroBooking('');
    setFechaEmbarque(fechaHoy());
    setIdCliente('');
    setIdProveedor('');
    setPaisOrigen('');
    setPaisDestino('');
    setNombreNave('');
    setIdPuertoOrigen('');
    setIdPuertoDestino('');
    setLugarEntrega('');
    setNombreBl('');
    setIdsProformaSeleccion([]);
    setArchivoPacking(null);
    setTotalPaquetes('');
    setTotalContenedores('');
    setDetalleExcel([]);
    setParseMeta(null);
    setArchivoParseError('');
    setErrors({});
    setSaveError('');
    setClienteBusqueda('');
    setClienteMenuOpen(false);
    setPaisOrigenMenuOpen(false);
    setPaisDestinoMenuOpen(false);
    setPuertoOrigenBusqueda('');
    setPuertoOrigenMenuOpen(false);
    setPuertoDestinoBusqueda('');
    setPuertoDestinoMenuOpen(false);
    setProformaBusqueda('');
    setProformaMenuOpen(false);
    contractSyncRef.current = { idsKey: '[]', parseMeta: null, label: '' };
  }, []);

  const onArchivoPackingChange = (e) => {
    const file = e.target.files?.[0] || null;
    setArchivoPacking(file);
    setArchivoParseError('');
    setDetalleExcel([]);
    setParseMeta(null);
    if (!file) {
      setTotalPaquetes('');
      setTotalContenedores('');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const buf = ev.target?.result;
      if (!buf) return;
      const res = parsePackingListExcel(buf);
      if (!res.ok) {
        setArchivoParseError(res.error || 'No se pudo interpretar el Excel.');
        return;
      }
      const rows = res.rows || [];
      const proformasAll = proformasRef.current || [];
      const idsSel = idsProformaRef.current || [];
      const idsSelSet = new Set(idsSel.map((id) => String(id)));
      const proformasParaContrato = proformasAll.filter((p) =>
        idsSelSet.has(String(p.id_proforma))
      );
      const contratosOpts = contratosPreseleccionadosLabels(proformasParaContrato);
      const idUltima = ultimaProformaId(idsSel);
      const poLabel = getPiLabel(idUltima, proformasAll);
      const productsDefault = getDefaultProductsCategoryEnFromPi(
        idUltima,
        proformasRef.current,
        productosRef.current,
        categoriasRef.current
      );
      setDetalleExcel(
        rows.map((row) => {
          const next = { ...row };
          if (contratosOpts.length > 0) {
            next.nf_celda = poLabel || '';
          } else if (poLabel) {
            next.nf_celda = poLabel;
          }
          if (productsDefault) next.productos_texto = productsDefault;
          return next;
        })
      );
      setParseMeta(res);
      if (res.totalPaquetes != null && !Number.isNaN(res.totalPaquetes)) {
        setTotalPaquetes(String(Math.round(res.totalPaquetes * 1000) / 1000));
      }
      if (res.contenedoresEstimado != null) {
        setTotalContenedores(String(res.contenedoresEstimado));
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const cargarPackingListEdicion = useCallback(async (idPl) => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const res = await fetch(`${urlApiProd}/packinglists/${idPl}`, { headers });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      setSaveError(err.error || 'No se pudo cargar el packing list.');
      return;
    }
    const pl = await res.json();
    bookingOriginalRef.current = pl.nro_booking || '';
    blOriginalRef.current = pl.nombre_bl || '';
    setNumeroBooking(pl.nro_booking || '');
    setFechaEmbarque((pl.fecha_embarque || '').slice(0, 10) || fechaHoy());
    setIdCliente(pl.id_cliente != null ? String(pl.id_cliente) : '');
    setClienteBusqueda(pl.cliente_nombre_snapshot || '');
    setIdProveedor(pl.id_proveedor != null ? String(pl.id_proveedor) : '');
    setPaisOrigen(pl.pais_origen || '');
    setPaisDestino(pl.pais_destino || '');
    setNombreNave(pl.nombre_nave || '');
    setIdPuertoOrigen(pl.id_puerto_origen != null ? String(pl.id_puerto_origen) : '');
    setPuertoOrigenBusqueda(pl.nombre_puerto_origen || '');
    setIdPuertoDestino(pl.id_puerto_destino != null ? String(pl.id_puerto_destino) : '');
    setPuertoDestinoBusqueda(pl.nombre_puerto_destino || '');
    setLugarEntrega(pl.lugar_entrega || '');
    setNombreBl(pl.nombre_bl || '');
    setIdsProformaSeleccion((pl.id_proformas || []).map((id) => String(id)));
    setTotalPaquetes(pl.total_paquetes != null ? String(pl.total_paquetes) : '');
    setTotalContenedores(pl.total_contenedores != null ? String(pl.total_contenedores) : '');
    setDetalleExcel(Array.isArray(pl.lineas) ? pl.lineas : []);
    setParseMeta(pl.parse_meta || null);
    setArchivoPacking(null);
    setArchivoParseError('');
    setErrors({});
    setSaveError('');
  }, []);

  useEffect(() => {
    if (!show) {
      packingModalLoadKeyRef.current = null;
      return undefined;
    }

    const loadKey = String(packingListEditId ?? 'new');
    if (packingModalLoadKeyRef.current === loadKey) return undefined;
    packingModalLoadKeyRef.current = loadKey;

    let cancelled = false;
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    setSaveError('');

    (async () => {
      try {
        const [r1, r2, r3, r4, r5, r6] = await Promise.all([
          fetch(`${urlApiProd}/clientes`, { headers }),
          fetch(`${urlApiProd}/proveedores`, { headers }),
          fetch(`${urlApiProd}/puertos`, { headers }),
          fetch(`${urlApiProd}/proformas`, { headers }),
          fetch(`${urlApiProd}/productos`, { headers }),
          fetch(`${urlApiProd}/categorias`, { headers }),
        ]);
        if (cancelled) return;
        if (r1.ok) setClientes(await r1.json());
        if (r2.ok) setProveedores(await r2.json());
        if (r3.ok) setPuertos(await r3.json());
        if (r4.ok) setProformas(await r4.json());
        if (r5.ok) setProductos(await r5.json());
        if (r6.ok) setCategorias(await r6.json());
        if (packingListEditId) {
          await cargarPackingListEdicion(packingListEditId);
        } else {
          resetForm();
          bookingOriginalRef.current = '';
          blOriginalRef.current = '';
        }
      } catch (e) {
        if (!cancelled) console.error('Error cargando datos packing list:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- una carga por apertura (loadKey)
  }, [show, packingListEditId]);

  /** Tras leer Excel o cambiar PI/catálogos: Products = categoría EN mayúsc. cuando aplique (última PI). */
  useEffect(() => {
    const productsDefault = getDefaultProductsCategoryEnFromPi(
      idProformaUltima,
      proformas,
      productos,
      categorias
    );
    if (!productsDefault) return;
    setDetalleExcel((prev) => {
      if (prev.length === 0) return prev;
      return prev.map((row) => ({
        ...row,
        productos_texto: productsDefault,
      }));
    });
  }, [idProformaUltima, proformas, productos, categorias, parseMeta]);

  /** Columna Contract: sin aplicar PI del formulario al importar Excel; sí al cambiar orden/selección de PIs. */
  useEffect(() => {
    const label = getPiLabel(idProformaUltima, proformas);
    const idsKey = JSON.stringify(idsProformaSeleccion);
    const prev = contractSyncRef.current;
    const idsChanged = prev.idsKey !== idsKey;
    const metaChanged = prev.parseMeta !== parseMeta;

    if (metaChanged) {
      contractSyncRef.current = { idsKey, parseMeta, label };
      return;
    }

    contractSyncRef.current = { idsKey, parseMeta, label };

    if (!idsChanged) return;

    setDetalleExcel((prevRows) => {
      if (prevRows.length === 0) return prevRows;
      return prevRows.map((row) => {
        const next = { ...row };
        if (idProformaUltima && label) {
          next.nf_celda = label;
        } else if (!idProformaUltima) {
          next.nf_celda = '';
        }
        return next;
      });
    });
  }, [idsProformaSeleccion, idProformaUltima, proformas, parseMeta]);

  const onChangeNumeroBooking = (e) => {
    setNumeroBooking(e.target.value);
    if (errors.numeroBooking) setErrors((o) => ({ ...o, numeroBooking: null }));
  };

  const onSelectCliente = (id) => {
    setIdCliente(id);
    const c = clientes.find((x) => String(x.id_cliente) === String(id));
    if (c) {
      setPaisDestino(normalizeCountryForPackingList(titleCasePaisNombre(c.pais || '')));
      const pid = c.puerto != null ? String(c.puerto) : '';
      setIdPuertoDestino(pid);
      if (pid) {
        const pto = puertos.find((x) => String(x.id_puerto) === pid);
        setPuertoDestinoBusqueda(pto ? puertoEtiqueta(pto) : '');
      } else {
        setPuertoDestinoBusqueda('');
      }
      setLugarEntrega(c.direccion_despacho || '');
    } else {
      setPaisDestino('');
      setIdPuertoDestino('');
      setPuertoDestinoBusqueda('');
      setLugarEntrega('');
    }
  };

  const onClienteBusquedaChange = (e) => {
    const v = e.target.value;
    setClienteBusqueda(v);
    setClienteMenuOpen(true);
    if (errors.idCliente) setErrors((o) => ({ ...o, idCliente: null }));
    if (idCliente) {
      const cur = clientes.find((x) => String(x.id_cliente) === String(idCliente));
      const same =
        cur && foldBusquedaTexto(cur.nombre || '') === foldBusquedaTexto(v);
      if (!same) onSelectCliente('');
    }
  };

  const elegirClienteLista = (c) => {
    onSelectCliente(String(c.id_cliente));
    setClienteBusqueda(c.nombre || '');
    setClienteMenuOpen(false);
    if (errors.idCliente) setErrors((o) => ({ ...o, idCliente: null }));
  };

  const onSelectProveedor = (id) => {
    setIdProveedor(id);
    const p = proveedores.find((x) => String(x.id_proveedor) === String(id));
    if (p) setPaisOrigen(normalizeCountryForPackingList(titleCasePaisNombre(p.pais || '')));
    else setPaisOrigen('');
  };

  const onPaisOrigenInputChange = (e) => {
    setPaisOrigen(normalizeCountryForPackingList(e.target.value));
    setPaisOrigenMenuOpen(true);
  };

  const elegirPaisOrigenLista = (nombre) => {
    setPaisOrigen(normalizeCountryForPackingList(nombre));
    setPaisOrigenMenuOpen(false);
  };

  const onPaisOrigenInputBlur = () => {
    window.setTimeout(() => {
      setPaisOrigenMenuOpen(false);
      setPaisOrigen((prev) => {
        const t = (prev || '').trim();
        if (!t) return '';
        return normalizeCountryForPackingList(titleCasePaisNombre(t));
      });
    }, 180);
  };

  const onPaisDestinoInputChange = (e) => {
    setPaisDestino(normalizeCountryForPackingList(e.target.value));
    setPaisDestinoMenuOpen(true);
  };

  const elegirPaisDestinoLista = (nombre) => {
    setPaisDestino(normalizeCountryForPackingList(nombre));
    setPaisDestinoMenuOpen(false);
  };

  const onPaisDestinoInputBlur = () => {
    window.setTimeout(() => {
      setPaisDestinoMenuOpen(false);
      setPaisDestino((prev) => {
        const t = (prev || '').trim();
        if (!t) return '';
        return normalizeCountryForPackingList(titleCasePaisNombre(t));
      });
    }, 180);
  };

  const onPuertoOrigenBusquedaChange = (e) => {
    const v = e.target.value;
    setPuertoOrigenBusqueda(v);
    setPuertoOrigenMenuOpen(true);
    if (idPuertoOrigen) {
      const p = puertos.find((x) => String(x.id_puerto) === String(idPuertoOrigen));
      const same =
        p && foldBusquedaTexto(puertoEtiqueta(p)) === foldBusquedaTexto(v);
      if (!same) setIdPuertoOrigen('');
    }
  };

  const elegirPuertoOrigenLista = (p) => {
    setIdPuertoOrigen(String(p.id_puerto));
    setPuertoOrigenBusqueda(puertoEtiqueta(p));
    setPuertoOrigenMenuOpen(false);
  };

  const onPuertoOrigenInputBlur = () => {
    window.setTimeout(() => setPuertoOrigenMenuOpen(false), 180);
  };

  const onPuertoDestinoBusquedaChange = (e) => {
    const v = e.target.value;
    setPuertoDestinoBusqueda(v);
    setPuertoDestinoMenuOpen(true);
    if (idPuertoDestino) {
      const p = puertos.find((x) => String(x.id_puerto) === String(idPuertoDestino));
      const same =
        p && foldBusquedaTexto(puertoEtiqueta(p)) === foldBusquedaTexto(v);
      if (!same) setIdPuertoDestino('');
    }
  };

  const elegirPuertoDestinoLista = (p) => {
    setIdPuertoDestino(String(p.id_puerto));
    setPuertoDestinoBusqueda(puertoEtiqueta(p));
    setPuertoDestinoMenuOpen(false);
  };

  const onPuertoDestinoInputBlur = () => {
    window.setTimeout(() => setPuertoDestinoMenuOpen(false), 180);
  };

  const onProformaBusquedaChange = (e) => {
    const v = e.target.value;
    setProformaBusqueda(v);
    setProformaMenuOpen(true);
  };

  const elegirProformaLista = (pi) => {
    proformaElegidaRecienteRef.current = true;
    setIdsProformaSeleccion((prev) => appendProformaIdOrdenado(prev, pi.id_proforma));
    setProformaBusqueda('');
    setProformaMenuOpen(true);
    if (errors.idProformas) setErrors((o) => ({ ...o, idProformas: null }));
    window.setTimeout(() => {
      proformaInputRef.current?.focus();
    }, 0);
    window.setTimeout(() => {
      proformaElegidaRecienteRef.current = false;
    }, 400);
  };

  const quitarProformaChip = (idProforma) => {
    setIdsProformaSeleccion((prev) => prev.filter((x) => String(x) !== String(idProforma)));
  };

  const onProformaInputBlur = () => {
    window.setTimeout(() => {
      if (proformaElegidaRecienteRef.current) {
        setProformaMenuOpen(true);
        return;
      }
      setProformaMenuOpen(false);
    }, 180);
  };

  const patchDetalleRow = useCallback((idx, patch) => {
    setDetalleExcel((prev) => {
      const next = [...prev];
      if (!next[idx]) return prev;
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  }, []);

  /** Mismo valor en todas las filas del mismo contenedor consecutivo (como en el Excel). */
  const patchDetalleByContainerGroup = useCallback((idx, patch) => {
    setDetalleExcel((prev) => {
      if (!prev[idx]) return prev;
      const next = [...prev];
      const k = normContRow(prev[idx]);
      if (!k) {
        next[idx] = { ...next[idx], ...patch };
        return next;
      }
      const s = groupStartContRows(prev, idx);
      const e = groupEndContRows(prev, idx);
      for (let i = s; i <= e; i += 1) {
        next[i] = { ...next[i], ...patch };
      }
      return next;
    });
  }, []);

  const validate = () => {
    const e = {};
    if (!numeroBooking.trim()) {
      e.numeroBooking = 'Ingrese el N° Booking.';
    } else {
      const bk = numeroBooking.trim();
      const bookingDuplicado =
        existingBookingNumbers.includes(bk) &&
        !(isEditMode && bk === bookingOriginalRef.current);
      if (bookingDuplicado) e.numeroBooking = 'Este N° Booking ya existe.';
    }
    if (!fechaEmbarque) e.fechaEmbarque = 'Indique fecha de embarque.';
    if (!idCliente) e.idCliente = 'Busque y elija un cliente de la lista.';
    if (!idsProformaSeleccion?.length) {
      e.idProformas = 'Seleccione al menos una PI de la lista.';
    }
    if (!nombreBl.trim()) e.nombreBl = 'Ingrese el nombre del BL.';
    const blNorm = nombreBl.trim();
    if (blNorm && numeroBooking.trim()) {
      const bk = numeroBooking.trim();
      const dup = existingBlPorInvoice.some(
        (row) =>
          String(row.nro_booking || '') === bk &&
          String(row.nombre_bl).toLowerCase() === blNorm.toLowerCase()
      );
      const blSinCambioEnEdicion =
        isEditMode &&
        bk === bookingOriginalRef.current &&
        blNorm.toLowerCase() === String(blOriginalRef.current).trim().toLowerCase();
      if (dup && !blSinCambioEnEdicion) {
        e.nombreBl = 'Este BL ya está registrado para este N°.';
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaveError('');
    setSaving(true);
    const payload = {
      nro_booking: numeroBooking.trim(),
      fecha_embarque: fechaEmbarque,
      id_cliente: idCliente ? parseInt(idCliente, 10) : null,
      id_proveedor: idProveedor ? parseInt(idProveedor, 10) : null,
      pais_origen: normalizeCountryForPackingList(paisOrigen),
      pais_destino: normalizeCountryForPackingList(paisDestino),
      nombre_nave: nombreNave,
      id_puerto_origen: idPuertoOrigen ? parseInt(idPuertoOrigen, 10) : null,
      id_puerto_destino: idPuertoDestino ? parseInt(idPuertoDestino, 10) : null,
      lugar_entrega: lugarEntrega,
      nombre_bl: nombreBl.trim(),
      id_proformas: idsProformaSeleccion.map((x) => parseInt(x, 10)).filter((n) => !Number.isNaN(n)),
      total_paquetes: totalPaquetes === '' ? null : Number.parseFloat(totalPaquetes),
      total_contenedores: totalContenedores === '' ? null : Number.parseFloat(totalContenedores),
      detalle_packing: detalleExcel,
      parse_meta: parseMeta,
    };
    try {
      const token = localStorage.getItem('token');
      const url = isEditMode
        ? `${urlApiProd}/packinglists/${packingListEditId}`
        : `${urlApiProd}/packinglists`;
      const res = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveError(body.error || `Error ${res.status}`);
        return;
      }
      setGuardadoToastMensaje(
        isEditMode ? 'Packing list editado correctamente' : 'Packing list creado exitosamente'
      );
      setShowGuardadoToast(true);
      if (typeof onGuardarExito === 'function') onGuardarExito();
      cerrar();
    } catch (e) {
      setSaveError(e.message || 'Error de red');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ToastContainer
        className="p-3"
        position="top-center"
        style={{ zIndex: 99999, maxWidth: 'unset' }}
      >
        <Toast
          style={{ width: 'min(35vw, 420px)' }}
          onClose={() => setShowGuardadoToast(false)}
          show={showGuardadoToast}
          delay={3500}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">Mensaje del sistema</strong>
          </Toast.Header>
          <Toast.Body className="text-center">{guardadoToastMensaje}</Toast.Body>
        </Toast>
      </ToastContainer>
      <ElegantModal
      show={show}
      onClose={cerrar}
      title={isEditMode ? 'Editar packing list' : 'Crear packing list'}
      subtitle={
        isEditMode
          ? 'Modifique los datos del envío y el detalle del packing list.'
          : 'Complete los datos del envío y cargue el archivo Excel del packing list.'
      }
      titleId="packinglist-title"
      size="xl"
      footer={
        loading ? null : (
          <div className="d-flex gap-2 justify-content-end flex-wrap">
            <Button variant="secondary" type="button" onClick={cerrar} disabled={saving}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" form="packinglist-form-add" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </div>
        )
      }
    >
      <form id="packinglist-form-add" onSubmit={handleSubmit}>
        {loading ? (
          <p className="text-center text-muted mb-0">Cargando datos…</p>
        ) : (
          <>
            {saveError ? (
              <Alert variant="danger" className="mb-3" dismissible onClose={() => setSaveError('')}>
                {saveError}
              </Alert>
            ) : null}
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>N° Booking</Form.Label>
                        <Form.Control
                          type="text"
                          value={numeroBooking}
                          onChange={onChangeNumeroBooking}
                          isInvalid={!!errors.numeroBooking}
                          placeholder="Ej: BK-2024-01"
                          autoComplete="off"
                        />
                        <Form.Control.Feedback type="invalid">{errors.numeroBooking}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Fecha embarque</Form.Label>
                        <Form.Control
                          type="date"
                          value={fechaEmbarque}
                          onChange={(e) => setFechaEmbarque(e.target.value)}
                          isInvalid={!!errors.fechaEmbarque}
                        />
                        <Form.Control.Feedback type="invalid">{errors.fechaEmbarque}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="position-relative">
                        <Form.Label>Cliente / Customer</Form.Label>
                        <Form.Control
                          type="text"
                          autoComplete="off"
                          placeholder="Escriba para buscar cliente…"
                          value={clienteBusqueda}
                          onChange={onClienteBusquedaChange}
                          onFocus={() => setClienteMenuOpen(true)}
                          onBlur={() => {
                            window.setTimeout(() => setClienteMenuOpen(false), 180);
                          }}
                          isInvalid={!!errors.idCliente}
                        />
                        {clienteMenuOpen && clientesFiltrados.length > 0 ? (
                          <ListGroup
                            className="position-absolute w-100 shadow border rounded overflow-auto"
                            style={{
                              zIndex: 2,
                              top: '100%',
                              marginTop: 2,
                              maxHeight: 220,
                            }}
                          >
                            {clientesFiltrados.map((c) => (
                              <ListGroup.Item
                                key={c.id_cliente}
                                action
                                className="py-2 px-3"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  elegirClienteLista(c);
                                }}
                              >
                                {c.nombre}
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : null}
                        <Form.Control.Feedback type="invalid">{errors.idCliente}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Proveedor (país origen)</Form.Label>
                        <Form.Select value={idProveedor} onChange={(e) => onSelectProveedor(e.target.value)}>
                          <option value="">Seleccione…</option>
                          {proveedores.map((p) => (
                            <option key={p.id_proveedor} value={p.id_proveedor}>
                              {p.nombre}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">País origen / Country of origin (desde proveedor, editable abajo)</Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="position-relative">
                        <Form.Label>País origen / Country of origin</Form.Label>
                        <Form.Control
                          type="text"
                          autoComplete="off"
                          value={paisOrigen}
                          onChange={onPaisOrigenInputChange}
                          onFocus={() => setPaisOrigenMenuOpen(true)}
                          onBlur={onPaisOrigenInputBlur}
                          placeholder="Escriba para filtrar o elija de la lista…"
                        />
                        {paisOrigenMenuOpen && paisesOrigenFiltrados.length > 0 ? (
                          <ListGroup
                            className="position-absolute w-100 shadow border rounded overflow-auto"
                            style={{
                              zIndex: 2,
                              top: '100%',
                              marginTop: 2,
                              maxHeight: 220,
                            }}
                          >
                            {paisesOrigenFiltrados.map((nombre) => (
                              <ListGroup.Item
                                key={nombre}
                                action
                                className="py-2 px-3"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  elegirPaisOrigenLista(nombre);
                                }}
                              >
                                {nombre}
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : null}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="position-relative">
                        <Form.Label>País destino / Destination country</Form.Label>
                        <Form.Control
                          type="text"
                          autoComplete="off"
                          value={paisDestino}
                          onChange={onPaisDestinoInputChange}
                          onFocus={() => setPaisDestinoMenuOpen(true)}
                          onBlur={onPaisDestinoInputBlur}
                          placeholder="Escriba para filtrar o elija de la lista…"
                        />
                        {paisDestinoMenuOpen && paisesDestinoFiltrados.length > 0 ? (
                          <ListGroup
                            className="position-absolute w-100 shadow border rounded overflow-auto"
                            style={{
                              zIndex: 2,
                              top: '100%',
                              marginTop: 2,
                              maxHeight: 220,
                            }}
                          >
                            {paisesDestinoFiltrados.map((nombre) => (
                              <ListGroup.Item
                                key={nombre}
                                action
                                className="py-2 px-3"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  elegirPaisDestinoLista(nombre);
                                }}
                              >
                                {nombre}
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : null}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Nombre nave / Vessel name</Form.Label>
                        <Form.Control value={nombreNave} onChange={(e) => setNombreNave(e.target.value)} />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="position-relative">
                        <Form.Label>Puerto origen / Loading port</Form.Label>
                        <Form.Control
                          type="text"
                          autoComplete="off"
                          value={puertoOrigenBusqueda}
                          onChange={onPuertoOrigenBusquedaChange}
                          onFocus={() => setPuertoOrigenMenuOpen(true)}
                          onBlur={onPuertoOrigenInputBlur}
                          placeholder="Escriba para buscar puerto…"
                        />
                        {puertoOrigenMenuOpen && puertosOrigenFiltrados.length > 0 ? (
                          <ListGroup
                            className="position-absolute w-100 shadow border rounded overflow-auto"
                            style={{
                              zIndex: 3,
                              top: '100%',
                              marginTop: 2,
                              maxHeight: 220,
                            }}
                          >
                            {puertosOrigenFiltrados.map((p) => (
                              <ListGroup.Item
                                key={p.id_puerto}
                                action
                                className="py-2 px-3"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  elegirPuertoOrigenLista(p);
                                }}
                              >
                                {puertoEtiqueta(p)}
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : null}
                        <Form.Text className="text-muted">
                          Lista según mantenedor Puertos: tipo Origen u Origen-Destino.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="position-relative">
                        <Form.Label>Puerto destino / Unloading port</Form.Label>
                        <Form.Control
                          type="text"
                          autoComplete="off"
                          value={puertoDestinoBusqueda}
                          onChange={onPuertoDestinoBusquedaChange}
                          onFocus={() => setPuertoDestinoMenuOpen(true)}
                          onBlur={onPuertoDestinoInputBlur}
                          placeholder="Escriba para buscar puerto…"
                        />
                        {puertoDestinoMenuOpen && puertosDestinoFiltrados.length > 0 ? (
                          <ListGroup
                            className="position-absolute w-100 shadow border rounded overflow-auto"
                            style={{
                              zIndex: 4,
                              top: '100%',
                              marginTop: 2,
                              maxHeight: 220,
                            }}
                          >
                            {puertosDestinoFiltrados.map((p) => (
                              <ListGroup.Item
                                key={`d-${p.id_puerto}`}
                                action
                                className="py-2 px-3"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  elegirPuertoDestinoLista(p);
                                }}
                              >
                                {puertoEtiqueta(p)}
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : null}
                        <Form.Text className="text-muted">
                          Lista según mantenedor Puertos: tipo Destino u Origen-Destino. Sugerido al elegir cliente;
                          puede cambiar.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Lugar de entrega / Place of delivery</Form.Label>
                        <Form.Control
                          value={lugarEntrega}
                          onChange={(e) => setLugarEntrega(e.target.value)}
                          placeholder="Desde dirección despacho del cliente"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Nombre BL (único por N°)</Form.Label>
                        <Form.Control
                          value={nombreBl}
                          onChange={(e) => setNombreBl(e.target.value)}
                          isInvalid={!!errors.nombreBl}
                         
                        />
                        <Form.Control.Feedback type="invalid">{errors.nombreBl}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="position-relative">
                        <Form.Label>PIs (varias; la última define Contract/Products por defecto)</Form.Label>
                        {idsProformaSeleccion.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1 mb-2">
                            {idsProformaSeleccion.map((idStr) => {
                              const pi = proformasFiltradas.find(
                                (p) => String(p.id_proforma) === String(idStr)
                              );
                              const lbl =
                                pi != null ? proformaEtiqueta(pi) : `id ${idStr}`;
                              return (
                                <Badge
                                  key={String(idStr)}
                                  bg="secondary"
                                  className="d-inline-flex align-items-center gap-1 fw-normal py-1 px-2"
                                >
                                  <span>{lbl}</span>
                                  <Button
                                    type="button"
                                    variant="link"
                                    className="text-white text-decoration-none p-0 lh-1"
                                    style={{ fontSize: '1rem', minWidth: '1rem' }}
                                    onClick={() => quitarProformaChip(idStr)}
                                    aria-label={`Quitar ${lbl}`}
                                  >
                                    ×
                                  </Button>
                                </Badge>
                              );
                            })}
                          </div>
                        ) : null}
                        <Form.Control
                          ref={proformaInputRef}
                          type="text"
                          autoComplete="off"
                          value={proformaBusqueda}
                          onChange={onProformaBusquedaChange}
                          onFocus={() => setProformaMenuOpen(true)}
                          onBlur={onProformaInputBlur}
                          placeholder="Buscar y agregar PI (número o correlativo)…"
                          isInvalid={!!errors.idProformas}
                        />
                        <Form.Control.Feedback type="invalid">{errors.idProformas}</Form.Control.Feedback>
                        {proformaMenuOpen && proformasBusquedaLista.length > 0 ? (
                          <ListGroup
                            className="position-absolute w-100 shadow border rounded overflow-auto"
                            style={{
                              zIndex: 5,
                              top: '100%',
                              marginTop: 2,
                              maxHeight: 220,
                            }}
                          >
                            {proformasBusquedaLista.map((pi) => (
                              <ListGroup.Item
                                key={pi.id_proforma}
                                action
                                className="py-2 px-3"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  elegirProformaLista(pi);
                                }}
                              >
                                {proformaEtiqueta(pi)}
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : null}
                        <Form.Text className="text-muted">
                          {idCliente
                            ? 'Solo PIs del cliente seleccionado. Re-elegir una PI la pasa al final (queda como última).'
                            : 'Elija un cliente para acotar la lista; sin cliente se muestran todas las PIs.'}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Carga de packing list (archivo)</Form.Label>
                        <Form.Control
                          type="file"
                          accept=".xlsx,.xls,.csv"
                          onChange={onArchivoPackingChange}
                        />
                        <Form.Text className="text-muted">
                          Se detecta la tabla por nombres de columnas (Paquetes, Pcs/paq, Total piezas,
                          Espesor, Ancho, Largo, Volumen/M3, Peso). Si hay dos tablas iguales, se usa la
                          inferior. Los totales de paquetes y contenedores se calculan al cargar el archivo.
                        </Form.Text>
                        {archivoParseError && (
                          <Alert variant="danger" className="mt-2 mb-0 py-2 small">
                            {archivoParseError}
                          </Alert>
                        )}
                        {parseMeta && detalleExcel.length > 0 && (
                          <Alert variant="success" className="mt-2 mb-0 py-2 small">
                            Tabla detectada en fila {parseMeta.headerRowIndex + 1}. {detalleExcel.length}{' '}
                            fila(s). Columnas:{' '}
                            {Object.entries(parseMeta.columnLetters || {})
                              .map(([k, letra]) => `${k}→${letra}`)
                              .join(', ')}
                          </Alert>
                        )}
                        {detalleExcel.length > 0 && (
                          <div className="mt-2 table-responsive" style={{ maxHeight: 400 }}>
                            <Table size="sm" bordered striped hover className="mb-0 small align-middle">
                              <thead>
                                <tr>
                                  <th className="text-nowrap">#</th>
                                  <th>Contract</th>
                                  <th>Products</th>
                                  <th>Paquetes</th>
                                  <th>Pcs/paq</th>
                                  <th>Total pcs</th>
                                  <th>Esp.</th>
                                  <th>Ancho</th>
                                  <th>Largo</th>
                                  <th>M³</th>
                                  <th>Gross kg</th>
                                  <th>Net weight</th>
                                  <th>Container</th>
                                </tr>
                              </thead>
                              <tbody>
                                {detalleExcel.map((row, idx) => {
                                  const cM = containerMergeInfo(detalleExcel, idx);
                                  const k = normContRow(row);
                                  const skipPesoBlock = k && !cM.isFirst;
                                  const s = cM.start;
                                  const fromStart = detalleExcel[s];
                                  return (
                                    <tr key={`${row._filaExcel}-${idx}`}>
                                      <td className="text-muted">{row._filaExcel}</td>
                                      <td style={{ minWidth: 100 }}>
                                        {contratosExcelOpciones.length > 0 ? (
                                          <Form.Select
                                            size="sm"
                                            className="py-0 px-1"
                                            value={row.nf_celda ?? ''}
                                            onChange={(e) =>
                                              patchDetalleRow(idx, { nf_celda: e.target.value })
                                            }
                                            aria-label={`Contract fila ${row._filaExcel}`}
                                          >
                                            <option value="">Seleccionar PI</option>
                                            {row.nf_celda != null &&
                                            String(row.nf_celda).trim() !== '' &&
                                            !contratosExcelOpciones.includes(String(row.nf_celda).trim()) ? (
                                              <option value={String(row.nf_celda).trim()}>
                                                {String(row.nf_celda).trim()} (Excel)
                                              </option>
                                            ) : null}
                                            {contratosExcelOpciones.map((opt) => (
                                              <option key={opt} value={opt}>
                                                {opt}
                                              </option>
                                            ))}
                                          </Form.Select>
                                        ) : (
                                          <Form.Control
                                            size="sm"
                                            className="py-0 px-1"
                                            value={row.nf_celda ?? ''}
                                            onChange={(e) =>
                                              patchDetalleRow(idx, { nf_celda: e.target.value })
                                            }
                                            aria-label={`Contract fila ${row._filaExcel}`}
                                          />
                                        )}
                                      </td>
                                      <td style={{ minWidth: 120 }}>
                                        <Form.Control
                                          size="sm"
                                          className="py-0 px-1"
                                          value={
                                            row.productos_texto ??
                                            defaultProductsLabel ??
                                            [
                                              row.grado_celda,
                                              row.proveedor_celda,
                                              row.almacen_celda,
                                            ]
                                              .filter(Boolean)
                                              .join(' / ')
                                          }
                                          onChange={(e) => patchDetalleRow(idx, { productos_texto: e.target.value })}
                                          aria-label={`Products fila ${row._filaExcel}`}
                                        />
                                      </td>
                                      <td>{row.cantidad_paquetes ?? ''}</td>
                                      <td>{row.piezas_por_paquete}</td>
                                      <td>{isRwNrRow(row) ? '' : (row.total_piezas ?? '')}</td>
                                      <td>{row.espesor ?? ''}</td>
                                      <td>{row.ancho}</td>
                                      <td>{row.largo ?? ''}</td>
                                      <td>{formatM3Display(row.total_m3)}</td>
                                      {!skipPesoBlock ? (
                                        <>
                                          <td className="align-middle p-0" style={{ minWidth: 72 }} rowSpan={cM.span}>
                                            <Form.Control
                                              size="sm"
                                              className="py-0 px-1"
                                              type="number"
                                              step="any"
                                              inputMode="decimal"
                                              value={fromStart.peso == null ? '' : fromStart.peso}
                                              onChange={(e) => {
                                                const v = e.target.value;
                                                if (v === '') {
                                                  patchDetalleByContainerGroup(s, { peso: null });
                                                  return;
                                                }
                                                const n = parseFloat(v.replace(',', '.'));
                                                patchDetalleByContainerGroup(s, {
                                                  peso: Number.isFinite(n) ? n : null,
                                                });
                                              }}
                                              title={
                                                k && cM.span > 1
                                                  ? `Mismo contenedor: ${cM.span} filas`
                                                  : undefined
                                              }
                                              aria-label={`Gross kg (bloque contenedor, fila ${row._filaExcel})`}
                                            />
                                          </td>
                                          <td className="align-middle p-0" style={{ minWidth: 72 }} rowSpan={cM.span}>
                                            <Form.Control
                                              size="sm"
                                              className="py-0 px-1"
                                              type="number"
                                              step="any"
                                              inputMode="decimal"
                                              value={
                                                fromStart.peso_neto == null ? '' : fromStart.peso_neto
                                              }
                                              onChange={(e) => {
                                                const v = e.target.value;
                                                if (v === '') {
                                                  patchDetalleByContainerGroup(s, { peso_neto: null });
                                                  return;
                                                }
                                                const n = parseFloat(v.replace(',', '.'));
                                                patchDetalleByContainerGroup(s, {
                                                  peso_neto: Number.isFinite(n) ? n : null,
                                                });
                                              }}
                                              aria-label={`Net weight (bloque contenedor, fila ${row._filaExcel})`}
                                            />
                                          </td>
                                          <td className="align-middle p-0" style={{ minWidth: 88 }} rowSpan={cM.span}>
                                            <Form.Control
                                              size="sm"
                                              className="py-0 px-1"
                                              value={fromStart.container_celda ?? ''}
                                              onChange={(e) =>
                                                patchDetalleByContainerGroup(
                                                  s,
                                                  containerFieldsFromInput(e.target.value)
                                                )
                                              }
                                              aria-label={`Container (bloque, fila ${row._filaExcel})`}
                                            />
                                          </td>
                                        </>
                                      ) : null}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Total paquetes / Total packages</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="1"
                          value={totalPaquetes}
                          onChange={(e) => setTotalPaquetes(e.target.value)}
                          placeholder="0 o desde Excel"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Total contenedores / Container</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="1"
                          value={totalContenedores}
                          onChange={(e) => setTotalContenedores(e.target.value)}
                          placeholder="0 o desde Excel"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

          </>
        )}
      </form>
    </ElegantModal>
    </>
  );
};

export default AgregarPackingList;
